
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ComplianceFormData } from "@/types/compliance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ComplianceAIFormProps {
  onSubmit: (data: ComplianceFormData) => void;
  isLoading: boolean;
  error: Error | string | null;
  initialData?: ComplianceFormData | null;
}

const complianceStandards = [
  { id: "gdpr", label: "GDPR" },
  { id: "hipaa", label: "HIPAA" },
  { id: "pci-dss", label: "PCI DSS" },
  { id: "iso27001", label: "ISO 27001" },
  { id: "sox", label: "Sarbanes-Oxley (SOX)" },
  { id: "nist", label: "NIST Cybersecurity Framework" },
  { id: "ccpa", label: "CCPA/CPRA" },
  { id: "fedramp", label: "FedRAMP" },
  { id: "csrd", label: "CSRD" },
  { id: "nis2", label: "NIS2 Directive" },
  { id: "aifmd", label: "AIFMD" },
  { id: "dora", label: "DORA" },
];

const tools = [
  { id: "spreadsheets", label: "Spreadsheets (Excel, Google Sheets)" },
  { id: "governance", label: "GRC Software" },
  { id: "docs", label: "Document Management Systems" },
  { id: "project", label: "Project Management Software" },
  { id: "email", label: "Email/Calendar" },
  { id: "manual", label: "Manual Processes/Paper" },
  { id: "chat", label: "Team Chat Tools" },
  { id: "custom", label: "Custom In-house Solutions" },
];

const challenges = [
  { id: "time", label: "Time-consuming processes" },
  { id: "inconsistency", label: "Inconsistent application" },
  { id: "documentation", label: "Documentation challenges" },
  { id: "audit", label: "Audit preparation" },
  { id: "updates", label: "Keeping up with regulatory changes" },
  { id: "reporting", label: "Reporting difficulties" },
  { id: "coordination", label: "Team coordination" },
  { id: "visibility", label: "Lack of visibility into processes" },
];

const ComplianceAIForm = ({ onSubmit, isLoading, error, initialData }: ComplianceAIFormProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const form = useForm<ComplianceFormData>({
    defaultValues: {
      teamSize: "",
      industry: "",
      complianceStandards: [],
      currentTools: [],
      currentChallenges: [],
      processDuration: "",
      complianceProcesses: "",
      softwareProcesses: "",
      additionalInfo: "",
    },
  });

  // Apply initial data if provided (for demo purposes)
  useEffect(() => {
    if (initialData) {
      Object.entries(initialData).forEach(([key, value]) => {
        form.setValue(key as keyof ComplianceFormData, value);
      });
    }
  }, [initialData, form]);

  const handleSubmit = (data: ComplianceFormData) => {
    onSubmit(data);
  };

  return (
    <Card className="

    border-border/40 shadow-xl rounded-2xl overflow-hidden border border-green-100/30 dark:border-green-900/20">
      <div className="w-full h-1 bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302]"></div>
      <CardContent className="pt-8 pb-24 md:pb-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Essential Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all">
                          <SelectValue placeholder="Select your industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="finance">Finance/Banking</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="government">Government</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="energy">Energy/Utilities</SelectItem>
                        <SelectItem value="telecom">Telecommunications</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="teamSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Compliance Team Size</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all">
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1-5">1-5 people</SelectItem>
                        <SelectItem value="6-15">6-15 people</SelectItem>
                        <SelectItem value="16-30">16-30 people</SelectItem>
                        <SelectItem value="31-50">31-50 people</SelectItem>
                        <SelectItem value="50+">More than 50 people</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="processDuration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Compliance Process Duration</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all">
                          <SelectValue placeholder="Select average duration" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="days">Days</SelectItem>
                        <SelectItem value="weeks">Weeks</SelectItem>
                        <SelectItem value="months">Months</SelectItem>
                        <SelectItem value="continuous">Continuous Process</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Main Required Fields - Process Description Textareas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="complianceProcesses"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Compliance Process(es)</FormLabel>
                    <FormDescription className="text-xs">
                      Describe some of the processes you would like to improve
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="We currently use..."
                        className="resize-none h-24 rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="softwareProcesses"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Compliance Software</FormLabel>
                    <FormDescription className="text-xs">
                      Describe some of the software and tools you would like to improve
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="We currently use..."
                        className="resize-none h-24 rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Toggle Advanced Options Button */}
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm border-green-200 dark:border-green-800/30 text-[#1b6504] dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/10"
              >
                {showAdvanced ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span>Hide Advanced Options</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>Show Advanced Options</span>
                  </>
                )}
              </Button>
            </div>

            {/* Advanced Options */}
            {showAdvanced && (
              <>
                <FormField
                  control={form.control}
                  name="complianceStandards"
                  render={() => (
                    <FormItem>
                      <div className="mb-2">
                        <FormLabel>Compliance Standards</FormLabel>
                        <FormDescription className="text-xs">
                          Select all standards your team manages
                        </FormDescription>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {complianceStandards.map((standard) => (
                          <FormField
                            key={standard.id}
                            control={form.control}
                            name="complianceStandards"
                            render={({ field }) => {
                              return (
                                <FormItem
                                  key={standard.id}
                                  className="flex flex-row items-start space-x-2 space-y-0 bg-gray-50/80 dark:bg-gray-900/50 rounded-lg p-2 border border-gray-100 dark:border-gray-800 hover:border-green-200 dark:hover:border-green-800/30 transition-colors"
                                >
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(standard.id)}
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([...field.value, standard.id])
                                          : field.onChange(
                                              field.value?.filter(
                                                (value) => value !== standard.id
                                              )
                                            )
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal text-sm">
                                    {standard.label}
                                  </FormLabel>
                                </FormItem>
                              )
                            }}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="currentTools"
                    render={() => (
                      <FormItem>
                        <div className="mb-2">
                          <FormLabel>Current Tools</FormLabel>
                          <FormDescription className="text-xs">
                            What tools do you currently use for compliance management?
                          </FormDescription>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {tools.map((tool) => (
                            <FormField
                              key={tool.id}
                              control={form.control}
                              name="currentTools"
                              render={({ field }) => {
                                return (
                                  <FormItem
                                    key={tool.id}
                                    className="flex flex-row items-start space-x-2 space-y-0 bg-gray-50/80 dark:bg-gray-900/50 rounded-lg p-2 border border-gray-100 dark:border-gray-800 hover:border-green-200 dark:hover:border-green-800/30 transition-colors"
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(tool.id)}
                                        onCheckedChange={(checked) => {
                                          return checked
                                            ? field.onChange([...field.value, tool.id])
                                            : field.onChange(
                                                field.value?.filter(
                                                  (value) => value !== tool.id
                                                )
                                              )
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="font-normal text-sm">
                                      {tool.label}
                                    </FormLabel>
                                  </FormItem>
                                )
                              }}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currentChallenges"
                    render={() => (
                      <FormItem>
                        <div className="mb-2">
                          <FormLabel>Current Challenges</FormLabel>
                          <FormDescription className="text-xs">
                            What are your biggest compliance management challenges?
                          </FormDescription>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {challenges.map((challenge) => (
                            <FormField
                              key={challenge.id}
                              control={form.control}
                              name="currentChallenges"
                              render={({ field }) => {
                                return (
                                  <FormItem
                                    key={challenge.id}
                                    className="flex flex-row items-start space-x-2 space-y-0 bg-gray-50/80 dark:bg-gray-900/50 rounded-lg p-2 border border-gray-100 dark:border-gray-800 hover:border-green-200 dark:hover:border-green-800/30 transition-colors"
                                  >
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(challenge.id)}
                                        onCheckedChange={(checked) => {
                                          return checked
                                            ? field.onChange([...field.value, challenge.id])
                                            : field.onChange(
                                                field.value?.filter(
                                                  (value) => value !== challenge.id
                                                )
                                              )
                                        }}
                                      />
                                    </FormControl>
                                    <FormLabel className="font-normal text-sm">
                                      {challenge.label}
                                    </FormLabel>
                                  </FormItem>
                                )
                              }}
                            />
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="additionalInfo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Information</FormLabel>
                      <FormDescription className="text-xs">
                        Any other details that would help us understand your compliance needs
                      </FormDescription>
                      <FormControl>
                        <Textarea
                          placeholder="Additional context..."
                          className="resize-none h-16 rounded-xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:ring-2 focus:ring-[#1b6504] focus:border-transparent transition-all"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-md">
                {typeof error === 'string' ? error : error.message}
              </div>
            )}

            <div className="flex justify-center mt-8">
              <Button
                type="submit"
                className="relative w-full max-w-sm mx-auto bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] rounded-full text-white font-medium shadow-lg hover:shadow-green-500/20 hover:translate-y-[-1px] transition-all active:translate-y-0 active:shadow-inner overflow-hidden group py-6"
                size="lg"
                disabled={isLoading}
              >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#184c06] via-[#1b6504] to-[#229302] animate-pulse opacity-90"></div>
                  <div className="animate-spin mr-3 relative z-10">
                    <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <span className="relative z-10 font-medium">AI Analyzing Your Compliance Process...</span>
                </div>
              ) : (
                <span className="relative z-10">
                  <span className="absolute inset-0 w-full h-full bg-white/10 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
                  <span className="relative">Analyze My Compliance Process</span>
                </span>
              )}
            </Button>
            </div>
          </form>
        </Form>
      </CardContent>
      <div className="w-full h-1 bg-gradient-to-r from-[#229302] to-[#184c06]"></div>
    </Card>
  );
};

export default ComplianceAIForm;
